// Per-listing edit suite (Haim feedback #8) — ONE clean dialog replacing the legacy
// trio (ListingMatchDescriptionOverride / ListingPicturesVisibilityDialog /
// ListingCardNotes + the like/visit buttons). Everything is edited locally and
// persisted in a single Firestore update on שמירה (saveListingMinisiteEdits), writing
// the exact favorites-doc field paths the public minisite reads.
//
// Watermark HONESTY (Haim runtime round + reject rounds): clean versions live on the
// listing doc (watermarkFreeImages, generated by the existing הסרת סימני מים studio)
// as a shared cross-user cache, and display automatically — but only for the user
// who performed removal on the listing (useListingWatermarkInfo is per-user; the old
// minisite-wide show/hide switch is gone). Images without a clean version always
// fall back to the watermarked original. This dialog reads the live doc, previews
// thumbs exactly as the customer will see them, marks each image with/without
// watermark, and one-click-opens the existing removal studio.
//
// DISCOVERABILITY (Haim: "I still want to be able to remove watermark on pictures from
// specific listings only ... when editing the listing in the minisite. I think we had it
// previously"): the per-listing removal never went away, but it sat at the very BOTTOM of
// this long form and was hidden outright whenever the live listing doc hadn't answered yet
// (or the listing has no marketplace doc). It is now the FIRST thing in the pictures
// section, via the shared ListingWatermarkPanel — which is never silent: it shows a
// disabled button while loading and explains itself when there is nothing to remove.
// The bulk "all listings at once" run in the tab header is an ADDITION, not a replacement.
import { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Button,
  Dialog,
  Divider,
  Tooltip,
  TextField,
  Typography,
  IconButton,
  DialogTitle,
  DialogActions,
  DialogContent,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress
} from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import {
  Close,
  Restore,
  ThumbUp,
  Opacity,
  ThumbDown,
  PinDrop,
  Visibility,
  CheckCircle,
  VisibilityOff,
  ThumbUpAltOutlined,
  ThumbDownOutlined
} from "@material-ui/icons";
import { useSnackbar } from "notistack5";
import { useListingWatermarkInfo } from "components/_dashboard/watermarks/useListingWatermarkInfo";
import { ListingWatermarkPanel } from "components/_dashboard/watermarks/ListingWatermarkPanel";
import type {
  RealEstateContact,
  FavoriteListingsOfContact
} from "../../../../../../@types/sharedSchema";
import type { ListingPicturesVisibility } from "components/_dashboard/listingsMatch/ListingPicturesVisibilty";
import type { MatchHit } from "./matchesSearch";
import { readListingMinisiteEdits, saveListingMinisiteEdits } from "./matchesData";
import useWebotServer from "hooks/useWebotServer";
import { buildListingAddressLine } from "./matchesDisplay";
import { getContactFirstName } from "./matchesShare";

type ListingEditDialogProps = {
  open: boolean;
  contact: RealEstateContact;
  listing: MatchHit | null;
  favorites: FavoriteListingsOfContact | null | undefined;
  onClose: () => void;
};

const VISIBILITY_LABELS: Record<ListingPicturesVisibility, string> = {
  all: "הצגת כל התמונות",
  some: "הסתרת חלק",
  none: "הסתרת הכל"
};

export default function ListingEditDialog({
  open,
  contact,
  listing,
  favorites,
  onClose
}: ListingEditDialogProps): JSX.Element {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();

  const listingId = listing?.objectID ?? "";
  const originalDescription = listing?.description?.trim() ?? "";
  const images = listing?.imageUrls ?? [];

  const [visited, setVisited] = useState(false);
  const [liked, setLiked] = useState(false);
  const [disliked, setDisliked] = useState(false);
  const [notes, setNotes] = useState("");
  const [description, setDescription] = useState("");
  const [mode, setMode] = useState<ListingPicturesVisibility>("all");
  const [hiddenPictures, setHiddenPictures] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const { saveListingPresentation } = useWebotServer();

  // Live watermark truth from the listing doc (yad2-listings-latest): which of this
  // listing's images actually have a clean version. Listener only while open —
  // clean versions generated in the studio (even by a colleague) appear immediately.
  const watermarkInfo = useListingWatermarkInfo(open && listingId ? listingId : undefined);
  const { cleanBySource, cleanedCount, totalCount: cleanableTotal } = watermarkInfo;
  // False for listings with no marketplace doc (e.g. webot-only) — no watermark UI then.
  const watermarkTracked = cleanableTotal > 0;
  const allCleaned = watermarkTracked && cleanedCount === cleanableTotal;

  // Seed local state from the favorites doc whenever the dialog opens.
  useEffect(() => {
    if (!open || !favorites || !listingId) return;
    const edits = readListingMinisiteEdits(favorites, listingId);
    setVisited(edits.visited);
    setLiked(edits.liked);
    setDisliked(edits.disliked);
    setNotes(edits.listingNotes);
    setDescription(edits.descriptionOverride || originalDescription);
    setMode(edits.picturesVisibilityMode);
    setHiddenPictures(edits.hiddenPictures);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, listingId]);

  if (!listing || !favorites) return <></>;

  const togglePicture = (src: string): void => {
    setHiddenPictures((prev) =>
      prev.includes(src) ? prev.filter((p) => p !== src) : [...prev, src]
    );
  };

  const handleModeChange = (newMode: ListingPicturesVisibility): void => {
    // ⚠️ Do NOT clear the hidden set on a mode flip — `mode` alone decides whether it applies, and
    // clearing it lost a hand-picked list with no undo. Now that the set is GLOBAL, a stray click
    // would have lost it for every customer at once.
    setMode(newMode);
  };

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    try {
      const trimmed = description.trim();
      /*
       * TWO SCOPES, SAVED SEPARATELY — and that split is the point.
       *
       * How the LISTING looks (description, which photos buyers see) is a fact about the property:
       * it is saved once and applied to every minisite of this realtor holding it, including links
       * already sent. How this BUYER relates to it (the realtor's private note, visited/liked/
       * disliked) is a fact about a person and stays on their minisite alone.
       *
       * Before 2026-09-30 this dialog wrote both into one contact's document, which is why editing
       * a listing for forty buyers meant opening it forty times.
       */
      const presentation = await saveListingPresentation({
        listingId,
        // "Same as original" means no override (legacy convention — reset works).
        descriptionOverride: trimmed === originalDescription ? "" : trimmed,
        picturesVisibilityMode: mode,
        hiddenPictures
      });
      await saveListingMinisiteEdits(contact.id, listingId, {
        listingNotes: notes,
        visited,
        liked,
        disliked
      });
      enqueueSnackbar(
        presentation.appliedToMinisites > 1
          ? `השינויים נשמרו והוחלו על ${presentation.appliedToMinisites} מיניסייטים`
          : "השינויים נשמרו",
        { variant: "success" }
      );
      onClose();
    } catch (err) {
      console.error("MatchesTab: failed to save listing edits", err);
      enqueueSnackbar("שמירת השינויים נכשלה, נסו שוב", {
        variant: "error",
        autoHideDuration: 4000
      });
    } finally {
      setSaving(false);
    }
  };

  const firstName = getContactFirstName(contact) || "הלקוח";

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ py: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack>
            <Typography variant="h6">עריכת נכס במיניסייט</Typography>
            <Typography variant="caption" color="text.secondary">
              {buildListingAddressLine(listing)}
            </Typography>
          </Stack>
          <IconButton onClick={onClose} disabled={saving}>
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          {/* buyer state */}
          <Stack spacing={1}>
            <Typography variant="subtitle2">סטטוס אצל {firstName}</Typography>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
              <Button
                size="small"
                variant={visited ? "contained" : "outlined"}
                color={visited ? "primary" : "inherit"}
                startIcon={<PinDrop />}
                onClick={() => setVisited((v) => !v)}
              >
                ביקר בנכס
              </Button>
              <Tooltip title="אהב את הנכס">
                <IconButton
                  color={liked ? "success" : "default"}
                  onClick={() => {
                    setLiked((v) => !v);
                    setDisliked(false);
                  }}
                >
                  {liked ? <ThumbUp /> : <ThumbUpAltOutlined />}
                </IconButton>
              </Tooltip>
              <Tooltip title="לא אהב / לא רלוונטי">
                <IconButton
                  color={disliked ? "error" : "default"}
                  onClick={() => {
                    setDisliked((v) => !v);
                    setLiked(false);
                  }}
                >
                  {disliked ? <ThumbDown /> : <ThumbDownOutlined />}
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>

          {/* realtor note */}
          <Stack spacing={1}>
            <Typography variant="subtitle2">הערה פנימית (מוצגת רק לך)</Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              maxRows={5}
              size="small"
              value={notes}
              placeholder="למשל: לתאם ביקור נוסף, לבדוק גמישות במחיר..."
              inputProps={{ dir: "auto" }}
              onChange={(e) => setNotes(e.target.value)}
            />
          </Stack>

          <Divider />

          {/* description override */}
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="subtitle2">תיאור הנכס (מוצג ללקוח במיניסייט)</Typography>
              <Button
                size="small"
                color="inherit"
                startIcon={<Restore />}
                disabled={description.trim() === originalDescription}
                onClick={() => setDescription(originalDescription)}
              >
                שחזור התיאור המקורי
              </Button>
            </Stack>
            <TextField
              fullWidth
              multiline
              minRows={3}
              maxRows={10}
              size="small"
              value={description}
              placeholder="תיאור מותאם ללקוח..."
              inputProps={{ dir: "auto" }}
              onChange={(e) => setDescription(e.target.value)}
            />
            <Typography variant="caption" color="text.secondary">
              התיאור המותאם יוצע אוטומטית גם כשתוסיפו את הנכס למיניסייט של לקוחות אחרים.
            </Typography>
          </Stack>

          {/* WATERMARKS — its OWN section, above the picture-visibility controls, so the
              per-listing removal is the first thing seen instead of the last thing found.
              It reuses the shared ListingWatermarkPanel, which owns the entry point into
              the existing removal studio (all images at once OR image-by-image) and is
              never silent: a disabled button while the live listing doc loads, and an
              explanation when the listing has nothing removable — the old inline button
              simply did not render in either case.
              There is no display switch any more (Haim reject round): clean versions
              always display in the minisite the moment they exist, so the note is a
              plain, honest per-listing status line. */}
          {listingId && (
            <>
              <Divider />
              <ListingWatermarkPanel
                listingId={listingId}
                info={watermarkInfo}
                note={
                  watermarkTracked ? (
                    <Typography
                      variant="caption"
                      sx={{
                        pt: 0.5,
                        color: allCleaned
                          ? "success.main"
                          : cleanedCount > 0
                          ? "warning.main"
                          : "text.secondary"
                      }}
                    >
                      {allCleaned
                        ? "כל תמונות הנכס מוצגות במיניסייט ללא סימני מים"
                        : cleanedCount > 0
                        ? `${cleanedCount} מתוך ${cleanableTotal} תמונות מוצגות במיניסייט ללא סימני מים — לשאר עדיין אין גרסה נקייה`
                        : "עדיין לא הוסרו סימני מים מתמונות הנכס — הן מוצגות במיניסייט עם סימני מים עד שתסירו אותם"}
                    </Typography>
                  ) : undefined
                }
              />
            </>
          )}

          {/* pictures */}
          {images.length > 0 && (
            <>
              <Divider />
              <Stack spacing={1.25}>
                <Typography variant="subtitle2">תמונות במיניסייט</Typography>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  value={mode}
                  onChange={(_e, value) => value && handleModeChange(value)}
                >
                  {(Object.keys(VISIBILITY_LABELS) as ListingPicturesVisibility[]).map((m) => (
                    <ToggleButton key={m} value={m}>
                      {VISIBILITY_LABELS[m]}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>

                <Box
                  sx={{
                    display: "grid",
                    gap: 1,
                    gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))"
                  }}
                >
                  {images.map((src) => {
                    const hidden =
                      mode === "none" || (mode === "some" && hiddenPictures.includes(src));
                    const cleanUrl = cleanBySource.get(src);
                    // The thumb shows exactly what the minisite renders — the clean
                    // version whenever one exists (hidden lists stay keyed by
                    // ORIGINAL url).
                    const displaySrc = cleanUrl ?? src;
                    const watermarkIndicator = !watermarkTracked
                      ? null
                      : cleanUrl
                      ? { icon: <CheckCircle sx={{ fontSize: 15 }} />, color: theme.palette.success.main, tip: "מוצגת ללא סימני מים" }
                      : { icon: <Opacity sx={{ fontSize: 15 }} />, color: theme.palette.warning.main, tip: "עדיין עם סימני מים — אפשר להסיר בחלק סימני המים למעלה" };
                    return (
                      <Box
                        key={src}
                        onClick={mode === "some" ? () => togglePicture(src) : undefined}
                        sx={{
                          position: "relative",
                          height: 84,
                          borderRadius: 1,
                          overflow: "hidden",
                          cursor: mode === "some" ? "pointer" : "default",
                          backgroundImage: `url(${displaySrc})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                          filter: hidden ? "blur(3px) grayscale(0.4)" : "none",
                          opacity: hidden ? 0.7 : 1,
                          outline: `1px solid ${theme.palette.divider}`,
                          transition: "filter 150ms, opacity 150ms"
                        }}
                      >
                        {mode === "some" && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: 4,
                              insetInlineEnd: 4,
                              width: 26,
                              height: 26,
                              borderRadius: "50%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "common.white",
                              bgcolor: alpha(theme.palette.grey[900], 0.55)
                            }}
                          >
                            {hidden ? (
                              <VisibilityOff sx={{ fontSize: 16 }} />
                            ) : (
                              <Visibility sx={{ fontSize: 16 }} />
                            )}
                          </Box>
                        )}
                        {watermarkIndicator && (
                          <Tooltip title={watermarkIndicator.tip}>
                            <Box
                              sx={{
                                position: "absolute",
                                bottom: 4,
                                insetInlineStart: 4,
                                width: 22,
                                height: 22,
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: watermarkIndicator.color,
                                bgcolor: "common.white",
                                boxShadow: theme.customShadows.z8
                              }}
                            >
                              {watermarkIndicator.icon}
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    );
                  })}
                </Box>
                <Typography variant="caption" color="text.secondary">
                  לחצו על תמונה כדי להסתיר אותה מהלקוח (במצב &quot;הסתרת חלק&quot;). הסימון בפינה
                  מציין אילו תמונות כבר קיימות ללא סימני מים.
                </Typography>
              </Stack>
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button color="inherit" onClick={onClose} disabled={saving}>
          ביטול
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} /> : undefined}
        >
          שמירה
        </Button>
      </DialogActions>
    </Dialog>
  );
}
